import { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import type { ChartOptions } from "chart.js";
import { Line } from "react-chartjs-2";
import { ReportDailySeriesPoint, ReportSummary } from "../../services/reportApi";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

export type ReportChartMetric = {
  key: keyof ReportSummary;
  label: string;
  color: string;
};

const formatChartAxisLabel = (day: string) => {
  const [year, month, date] = day.split("-").map((part) => parseInt(part, 10));
  const parsed = new Date(year, month - 1, date);
  return parsed.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
};

const toCumulativeSeries = (
  series: ReportDailySeriesPoint[],
  selectedMetrics: ReportChartMetric[]
): ReportDailySeriesPoint[] => {
  const runningTotals: Partial<Record<keyof ReportSummary, number>> = {};

  return series.map((row) => {
    const cumulativeRow = { ...row };

    selectedMetrics.forEach((metric) => {
      const previous = runningTotals[metric.key] ?? 0;
      const dailyValue = row[metric.key as keyof ReportDailySeriesPoint] as number;
      const nextTotal = previous + dailyValue;
      runningTotals[metric.key] = nextTotal;
      (cumulativeRow as Record<string, number | string>)[metric.key] = nextTotal;
    });

    return cumulativeRow;
  });
};

interface ReportActivityChartProps {
  series: ReportDailySeriesPoint[];
  selectedMetrics: ReportChartMetric[];
  cumulative?: boolean;
  heightPx?: number;
}

export function ReportChartModeToggle({
  cumulative,
  onCumulativeChange,
}: {
  cumulative: boolean;
  onCumulativeChange: (cumulative: boolean) => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 shadow-sm">
      <span className={`text-xs font-medium ${!cumulative ? "text-gray-900" : "text-gray-500"}`}>
        Daily
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={cumulative}
        aria-label="Toggle cumulative graph"
        onClick={() => onCumulativeChange(!cumulative)}
        className={`relative h-5 w-9 rounded-full transition-colors ${
          cumulative ? "bg-[#EC6E51]" : "bg-gray-300"
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
            cumulative ? "translate-x-4" : "translate-x-0"
          }`}
        />
      </button>
      <span className={`text-xs font-medium ${cumulative ? "text-gray-900" : "text-gray-500"}`}>
        Cumulative
      </span>
    </div>
  );
}

export function ReportActivityChart({
  series,
  selectedMetrics,
  cumulative = false,
  heightPx = 320,
}: ReportActivityChartProps) {
  const chartSeries = useMemo(
    () => (cumulative ? toCumulativeSeries(series, selectedMetrics) : series),
    [series, selectedMetrics, cumulative]
  );

  const data = useMemo(
    () => ({
      labels: chartSeries.map((row) => formatChartAxisLabel(row.day)),
      datasets: selectedMetrics.map((metric) => ({
        label: metric.label,
        data: chartSeries.map(
          (row) => row[metric.key as keyof ReportDailySeriesPoint] as number
        ),
        borderColor: metric.color,
        backgroundColor: metric.color,
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointRadius: chartSeries.length > 20 ? 0 : 3,
        pointHoverRadius: chartSeries.length > 20 ? 0 : 4,
        pointHitRadius: 8,
        spanGaps: true,
      })),
    }),
    [chartSeries, selectedMetrics]
  );

  const options = useMemo<ChartOptions<"line">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { intersect: false, mode: "index" },
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          labels: {
            boxWidth: 12,
            boxHeight: 12,
            usePointStyle: true,
            padding: 16,
            font: { size: 11 },
          },
        },
        tooltip: {
          animation: false,
          callbacks: {
            label: (ctx) => {
              const value = typeof ctx.parsed.y === "number" ? ctx.parsed.y : 0;
              return `${ctx.dataset.label}: ${value.toLocaleString("en-IN")}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { color: "rgba(0,0,0,0.06)" },
          ticks: {
            maxRotation: chartSeries.length > 10 ? 40 : 0,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 16,
            font: { size: 10 },
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(0,0,0,0.06)" },
          ticks: {
            precision: 0,
            font: { size: 10 },
          },
        },
      },
    }),
    [chartSeries.length, selectedMetrics]
  );

  return (
    <div className="relative w-full min-w-0" style={{ height: heightPx }}>
      <Line data={data} options={options} />
    </div>
  );
}
